<script setup>
import { computed, ref, watch } from 'vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import { FIELD_INPUT_CLASS, FIELD_TEXTAREA_CLASS } from '../constants';
import { isQuestionVisible, isScoredType } from '../helper/surveyFormHelper';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Live preview of the public survey.
 *
 * Renders the draft exactly the way the customer will see it — including the
 * success screen — with working controls, so the operator can feel the flow
 * before sending a single link. Nothing here talks to the API: it is a pure
 * projection of the draft held by the builder.
 */
const props = defineProps({
  form: { type: Object, required: true },
});

const { t } = useSurveyLabels();

const answers = ref({});
const activeScreen = ref('form');

const questions = computed(() => props.form.questions || []);
const visibleQuestions = computed(() =>
  questions.value.filter(question => isQuestionVisible(question, answers.value))
);
const hasQuestions = computed(() => visibleQuestions.value.length > 0);

const tabs = computed(() => [
  {
    id: 'form',
    label: t('SURVEYS.PREVIEW.TAB_FORM'),
    icon: 'i-lucide-clipboard-list',
  },
  {
    id: 'success',
    label: t('SURVEYS.PREVIEW.TAB_SUCCESS'),
    icon: 'i-lucide-party-popper',
  },
]);

const scaleValues = question => {
  const min = Number(question?.scale?.min ?? 1);
  const max = Number(question?.scale?.max ?? 5);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return [];
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
};

const inputType = question => {
  if (question?.type === 'phone') return 'tel';
  if (['date', 'time', 'email', 'number'].includes(question?.type)) {
    return question.type;
  }
  return 'text';
};

const inputMode = question => {
  if (question?.type === 'email') return 'email';
  if (question?.type === 'phone') return 'tel';
  if (question?.type === 'number') return 'decimal';
  if (question?.type === 'document_id') return 'numeric';
  if (question?.type === 'time') return 'numeric';
  return undefined;
};

const inputPattern = question => {
  if (question?.type === 'document_id') return '[0-9.\\-/ ]+';
  if (question?.type === 'time') return '([01][0-9]|2[0-3]):[0-5][0-9]';
  return undefined;
};

const answerOf = key => answers.value[key];

const characterCount = question =>
  t('SURVEYS.PREVIEW.CHARACTER_COUNT', {
    current: String(answerOf(question.key) || '').length,
    max: question.max_length,
  });

const setAnswer = (key, value) => {
  answers.value = { ...answers.value, [key]: value };
};

const toggleMultiChoice = (key, value) => {
  const current = Array.isArray(answers.value[key]) ? answers.value[key] : [];
  const next = current.includes(value)
    ? current.filter(item => item !== value)
    : [...current, value];
  setAnswer(key, next);
};

const isChecked = (key, value) => {
  const current = answers.value[key];
  return Array.isArray(current) ? current.includes(value) : current === value;
};

const answeredCount = computed(
  () =>
    visibleQuestions.value.filter(question => {
      const value = answers.value[question.key];
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== '';
    }).length
);

const progress = computed(() =>
  hasQuestions.value
    ? Math.round((answeredCount.value / visibleQuestions.value.length) * 100)
    : 0
);

const sampleScore = computed(() => {
  const scored = visibleQuestions.value.find(question =>
    isScoredType(question.type)
  );
  if (!scored) return null;
  const value = answers.value[scored.key];
  return value === undefined || value === null || value === '' ? null : value;
});

const scoreMax = computed(() => {
  const scored = visibleQuestions.value.find(question =>
    isScoredType(question.type)
  );
  return scored?.scale?.max ?? 5;
});

const showScoreOnSuccess = computed(
  () => props.form.success_screen?.show_score && sampleScore.value !== null
);

const successTitle = computed(
  () =>
    props.form.success_screen?.title || t('SURVEYS.PREVIEW.SUCCESS_FALLBACK')
);

// A structural change (new/removed question) invalidates stale answers.
watch(
  () =>
    questions.value
      .map(question => `${question.key}:${question.type}`)
      .join('|'),
  () => {
    answers.value = {};
  }
);

watch(
  visibleQuestions,
  visible => {
    const visibleKeys = new Set(visible.map(question => question.key));
    const nextAnswers = Object.fromEntries(
      Object.entries(answers.value).filter(([key]) => visibleKeys.has(key))
    );
    if (Object.keys(nextAnswers).length !== Object.keys(answers.value).length) {
      answers.value = nextAnswers;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex min-w-0 flex-col gap-3">
    <LaisTabs
      v-model="activeScreen"
      :tabs="tabs"
      :aria-label="t('SURVEYS.PREVIEW.TITLE')"
      fill
      :lazy="false"
    >
      <template #form>
        <div
          class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
        >
          <div
            class="flex items-center gap-2 border-b border-lais-border-soft bg-lais-bg-soft px-3 py-2"
          >
            <span
              class="size-2 rounded-full bg-lais-border"
              aria-hidden="true"
            />
            <span
              class="size-2 rounded-full bg-lais-border"
              aria-hidden="true"
            />
            <span
              class="min-w-0 flex-1 truncate text-center text-lais-label-xs text-lais-text-muted"
            >
              {{ t('SURVEYS.PREVIEW.URL_HINT') }}
            </span>
          </div>

          <div class="flex flex-col gap-4 px-4 py-5">
            <div class="min-w-0">
              <h3
                class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
              >
                {{ form.name || t('SURVEYS.PREVIEW.UNTITLED') }}
              </h3>
              <p
                v-if="form.description"
                class="mt-1 text-lais-body-sm leading-relaxed text-lais-text-body"
              >
                {{ form.description }}
              </p>
            </div>

            <div
              v-if="hasQuestions"
              class="h-1 w-full overflow-hidden rounded-full bg-lais-bg-soft"
              role="progressbar"
              :aria-valuenow="progress"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-label="t('SURVEYS.PREVIEW.PROGRESS')"
            >
              <span
                class="block h-full rounded-full bg-lais-primary transition-all"
                :style="{ width: `${progress}%` }"
              />
            </div>

            <p
              v-if="!hasQuestions"
              class="rounded-lais border border-dashed border-lais-border px-3 py-6 text-center text-lais-body-sm text-lais-text-muted"
            >
              {{ t('SURVEYS.PREVIEW.EMPTY') }}
            </p>

            <fieldset
              v-for="question in visibleQuestions"
              :key="question.key"
              class="min-w-0 border-0 p-0"
            >
              <legend class="mb-2 min-w-0">
                <span class="text-[14px] font-semibold text-lais-text">
                  {{ question.label || t('SURVEYS.PREVIEW.UNTITLED_QUESTION') }}
                  <span v-if="question.required" class="text-lais-danger">{{
                    t('SURVEYS.PREVIEW.REQUIRED_MARKER')
                  }}</span>
                </span>
                <span
                  v-if="question.help"
                  class="mt-0.5 block text-[12px] leading-snug text-lais-text-muted"
                >
                  {{ question.help }}
                </span>
              </legend>

              <div
                v-if="question.type === 'rating'"
                class="flex flex-wrap gap-1"
              >
                <button
                  v-for="value in scaleValues(question)"
                  :key="value"
                  type="button"
                  class="grid size-9 place-items-center rounded-lais transition hover:bg-lais-warning/10 focus-visible:outline-none focus-visible:shadow-lais-focus"
                  :aria-label="String(value)"
                  :aria-pressed="answerOf(question.key) === value"
                  @click="setAnswer(question.key, value)"
                >
                  <span
                    class="size-6"
                    :class="
                      Number(answerOf(question.key)) >= value
                        ? 'i-lucide-star fill-current text-lais-warning'
                        : 'i-lucide-star text-lais-border'
                    "
                    aria-hidden="true"
                  />
                </button>
              </div>

              <div
                v-else-if="
                  question.type === 'nps' || question.type === 'likert'
                "
                class="flex flex-wrap gap-1.5"
              >
                <button
                  v-for="value in scaleValues(question)"
                  :key="value"
                  type="button"
                  class="grid h-9 min-w-9 place-items-center rounded-lais border px-2 text-[13px] font-semibold tabular-nums transition focus-visible:outline-none focus-visible:shadow-lais-focus"
                  :class="
                    answerOf(question.key) === value
                      ? 'border-lais-primary bg-lais-primary text-white'
                      : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
                  "
                  :aria-pressed="answerOf(question.key) === value"
                  @click="setAnswer(question.key, value)"
                >
                  {{ value }}
                </button>
              </div>

              <div v-else-if="question.type === 'boolean'" class="flex gap-2">
                <button
                  type="button"
                  class="h-9 flex-1 rounded-lais border text-[13px] font-semibold transition focus-visible:outline-none focus-visible:shadow-lais-focus"
                  :class="
                    isChecked(question.key, true)
                      ? 'border-lais-primary bg-lais-primary text-white'
                      : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
                  "
                  @click="setAnswer(question.key, true)"
                >
                  {{ t('SURVEYS.PREVIEW.YES') }}
                </button>
                <button
                  type="button"
                  class="h-9 flex-1 rounded-lais border text-[13px] font-semibold transition focus-visible:outline-none focus-visible:shadow-lais-focus"
                  :class="
                    isChecked(question.key, false)
                      ? 'border-lais-primary bg-lais-primary text-white'
                      : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
                  "
                  @click="setAnswer(question.key, false)"
                >
                  {{ t('SURVEYS.PREVIEW.NO') }}
                </button>
              </div>

              <div
                v-else-if="question.type === 'single_choice'"
                class="flex flex-col gap-1.5"
              >
                <button
                  v-for="option in question.options"
                  :key="option.value"
                  type="button"
                  class="flex items-center gap-2.5 rounded-lais border px-3 py-2 text-left text-[13px] transition focus-visible:outline-none focus-visible:shadow-lais-focus"
                  :class="
                    isChecked(question.key, option.value)
                      ? 'border-lais-primary bg-lais-primary-50/60 text-lais-text'
                      : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
                  "
                  @click="setAnswer(question.key, option.value)"
                >
                  <span
                    class="size-4 shrink-0"
                    :class="
                      isChecked(question.key, option.value)
                        ? 'i-lucide-circle-dot text-lais-primary'
                        : 'i-lucide-circle text-lais-border'
                    "
                    aria-hidden="true"
                  />
                  <span class="min-w-0 truncate">
                    {{ option.label || t('SURVEYS.PREVIEW.UNTITLED_OPTION') }}
                  </span>
                </button>
              </div>

              <div
                v-else-if="question.type === 'multi_choice'"
                class="flex flex-col gap-1.5"
              >
                <button
                  v-for="option in question.options"
                  :key="option.value"
                  type="button"
                  class="flex items-center gap-2.5 rounded-lais border px-3 py-2 text-left text-[13px] transition focus-visible:outline-none focus-visible:shadow-lais-focus"
                  :class="
                    isChecked(question.key, option.value)
                      ? 'border-lais-primary bg-lais-primary-50/60 text-lais-text'
                      : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
                  "
                  @click="toggleMultiChoice(question.key, option.value)"
                >
                  <span
                    class="size-4 shrink-0"
                    :class="
                      isChecked(question.key, option.value)
                        ? 'i-lucide-square-check-big text-lais-primary'
                        : 'i-lucide-square text-lais-border'
                    "
                    aria-hidden="true"
                  />
                  <span class="min-w-0 truncate">
                    {{ option.label || t('SURVEYS.PREVIEW.UNTITLED_OPTION') }}
                  </span>
                </button>
              </div>

              <textarea
                v-else-if="question.type === 'long_text'"
                :value="answerOf(question.key) || ''"
                rows="3"
                :maxlength="question.max_length || undefined"
                :class="FIELD_TEXTAREA_CLASS"
                :placeholder="t('SURVEYS.PREVIEW.TEXT_PLACEHOLDER')"
                @input="setAnswer(question.key, $event.target.value)"
              />

              <input
                v-else
                :value="answerOf(question.key) || ''"
                :type="inputType(question)"
                :inputmode="inputMode(question)"
                :autocomplete="
                  ['email', 'phone'].includes(question.type)
                    ? inputType(question)
                    : question.type === 'document_id'
                      ? 'off'
                      : undefined
                "
                :min="question.type === 'number' ? question.min : undefined"
                :max="question.type === 'number' ? question.max : undefined"
                :step="question.type === 'number' ? 'any' : undefined"
                :pattern="inputPattern(question)"
                :spellcheck="
                  question.type === 'document_id' ? false : undefined
                "
                :maxlength="
                  question.type === 'document_id'
                    ? 18
                    : question.max_length || undefined
                "
                :class="FIELD_INPUT_CLASS"
                :placeholder="
                  question.placeholder || t('SURVEYS.PREVIEW.TEXT_PLACEHOLDER')
                "
                @input="setAnswer(question.key, $event.target.value)"
              />

              <p
                v-if="
                  question.max_length &&
                  ['text', 'long_text'].includes(question.type)
                "
                class="mt-1 text-right text-[11px] tabular-nums text-lais-text-muted"
              >
                {{ characterCount(question) }}
              </p>
            </fieldset>

            <button
              type="button"
              class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lais bg-lais-primary text-[14px] font-semibold text-white shadow-lais-soft transition hover:bg-lais-primary-600 focus-visible:outline-none focus-visible:shadow-lais-focus disabled:opacity-50"
              :disabled="!hasQuestions"
              @click="activeScreen = 'success'"
            >
              {{ t('SURVEYS.PREVIEW.SUBMIT') }}
              <span class="i-lucide-arrow-right size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </template>

      <template #success>
        <div
          class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
        >
          <div class="flex flex-col items-center gap-3 px-4 py-8 text-center">
            <span
              class="grid size-14 place-items-center rounded-full bg-lais-mint/20 text-lais-mint-dark"
            >
              <span class="i-lucide-check size-7" aria-hidden="true" />
            </span>
            <h3
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              {{ successTitle }}
            </h3>
            <p
              v-if="form.success_screen?.message"
              class="max-w-sm text-lais-body-sm leading-relaxed text-lais-text-body"
            >
              {{ form.success_screen.message }}
            </p>

            <p
              v-if="showScoreOnSuccess"
              class="inline-flex items-center gap-2 rounded-full bg-lais-bg-soft px-3 py-1.5 text-[13px] font-semibold text-lais-text"
            >
              <span
                class="i-lucide-star size-3.5 fill-current text-lais-warning"
                aria-hidden="true"
              />
              {{
                t('SURVEYS.PREVIEW.SCORE', {
                  score: sampleScore,
                  max: scoreMax,
                })
              }}
            </p>

            <button
              v-if="form.success_screen?.cta_label"
              type="button"
              class="mt-1 inline-flex h-10 items-center justify-center rounded-lais bg-lais-primary px-4 text-[14px] font-semibold text-white transition hover:bg-lais-primary-600 focus-visible:outline-none focus-visible:shadow-lais-focus"
            >
              {{ form.success_screen.cta_label }}
            </button>
            <span
              v-if="form.success_screen?.cta_url"
              class="max-w-full truncate text-[11px] text-lais-text-muted"
            >
              {{ form.success_screen.cta_url }}
            </span>

            <button
              type="button"
              class="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-lais-link transition hover:text-lais-link-hover focus-visible:outline-none focus-visible:shadow-lais-focus"
              @click="activeScreen = 'form'"
            >
              <span class="i-lucide-rotate-ccw size-3.5" aria-hidden="true" />
              {{ t('SURVEYS.PREVIEW.RESTART') }}
            </button>
          </div>
        </div>
      </template>
    </LaisTabs>
  </div>
</template>
